import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Button,
  Popover,
} from "@mui/material";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import CustomTypography from "../../globalStyles/fonts";
import { isMobile } from "../../globalStyles/mobile";
import { toast } from "react-toastify";
import Pagination from "../pagination";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "../../globalStyles/icons/editIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { emailTemplateService } from "../../utils/services/email-templateService";
import EmailTemplateDetailsModal from "../email-template-details-modal";

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 8,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const EmailTemplateList = ({
  viewMode,
  data,
  onEditData,
  onDeleteData,
  setPage,
  setRowsPerPage,
  rowsPerPage,
  page,
  dataCount,
  selectedTemplate,
  setSelectedTemplate,
}) => {
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [templateData, setTemplateData] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });
  const [anchorPositionFilter, setAnchorPositionFilter] = useState(null);
  const startIndex = rowsPerPage * page;

  const deleteData = async () => {
    try {
      await emailTemplateService.delete(deletingItemId);
      setOpenConfirmation(false);
      onDeleteData && onDeleteData();
      toast.success("Email Template deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete");
      console.error("Delete failed:", error);
    }
  };
  const handleViewClick = (x) => {
    setSelectedTemplate(x);
    setOpenViewModal(true);
  };
  const handleDeleteClick = (item) => {
    setSelectedTemplate(item);
    setDeletingItemId(item._id);
    setOpenConfirmation(true);
  };
  const handlePopoverOpen = (event, item) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorEl(event.currentTarget);
    setAnchorPositionFilter({
      top: rect.top,
      left: rect.left,
    });
    setSelectedTemplate(item);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setAnchorPosition(null);
  };
  useEffect(() => {
    setTemplateData(
      data?.map((item) => ({
        ...item,
        action: (
          <Box>
            <IconButton
              aria-label="more actions"
              onClick={(e) => handlePopoverOpen(e, item)}
              sx={{
                color:
                  anchorEl && selectedTemplate?._id === item._id
                    ? "primary.main"
                    : "inherit",
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              anchorReference="anchorPosition"
              anchorPosition={
                anchorPositionFilter
                  ? {
                      top: anchorPositionFilter.top,
                      left: anchorPositionFilter.left,
                    }
                  : undefined
              }
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiPaper-root": {
                  boxShadow: "none",
                  border: "1px solid #EEEEEE",
                  borderRadius: "4px",
                },
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                padding="8px"
                minWidth="150px"
              >
                <Button
                  style={{
                    color: "#333333",
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  aria-label="view"
                  onClick={() => {
                    handleViewClick(selectedTemplate);
                    handlePopoverClose();
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <ViewIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      color={"#333333"}
                      fontWeight={500}
                    >
                      View
                    </Typography>
                  </Box>
                </Button>

                <Button
                  style={{
                    color: "#333333",
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  aria-label="edit"
                  onClick={() => {
                    onEditData && onEditData(selectedTemplate);
                    handlePopoverClose();
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <EditIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      color={"#333333"}
                      fontWeight={500}
                    >
                      Edit
                    </Typography>
                  </Box>
                </Button>
                <Button
                  style={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                  }}
                  aria-label="delete"
                  onClick={() => {
                    handleDeleteClick(selectedTemplate);
                    handlePopoverClose();
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    width="100%"
                    gap={1}
                  >
                    <DeleteIcon />
                    <Typography
                      fontFamily={"poppins"}
                      fontSize={"16px"}
                      fontWeight={500}
                      color={"#333333"}
                    >
                      Delete
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </Popover>
          </Box>
        ),
      })),
    );
  }, [data, anchorEl]);

  const renderGridView = () => (
    <Grid
      sx={{
        marginTop: "1px",
        maxHeight: "100%",
        paddingBottom: 5,
        overflow: "auto",
      }}
      container
      spacing={2}
    >
      {templateData?.map((template, index) => (
        <Grid sx={{ paddingBottom: 1 }} item xs={12} sm={6} md={4} key={index}>
          <Card
            sx={{
              boxShadow: 3,
              cursor: "pointer",
              width: "100%",
              height: "100%",
              position: "relative",
              borderRadius: "6px",
              border: "1px solid #D0D5DD",
            }}
          >
            <CardContent>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  fontWeight="500"
                  sx={{ textAlign: "start" }}
                  fontFamily="Poppins"
                  color="#333333"
                >
                  {startIndex + index + 1}
                </Typography>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  {template?.action}
                </div>
              </div>
              <Grid container spacing={1} mt={2}>
                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography
                    fontWeight="500"
                    sx={{ textAlign: "start" }}
                    fontFamily="Poppins"
                    color="#333333"
                  >
                    Template Name
                  </Typography>
                </Grid>

                <Grid item xs={7}>
                  <Tooltip title={template?.name} arrow>
                    <Typography
                      noWrap
                      sx={{ textAlign: "start" }}
                      fontWeight="500"
                      fontFamily={"poppins"}
                      color={"#333333"}
                    >
                      : {template?.name}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
  const NoDataImage = () => (
    <div
      style={{
        width: "100%",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <img
        src="no_data_rateCard.png"
        alt="No data available"
        style={{ objectFit: "fill", height: "300px" }}
      />
    </div>
  );

  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography
          fontSize="12px"
          color="var(--text-secondary)"
          fontWeight={400}
          fontFamily={"Poppins"}
        >
          {startIndex + index + 1}
        </Typography>
      ),
    },
    {
      key: "name",
      title: "Name",
      // handleOpenFilter: handleOpenFilter,
      displayKey: "name",
    },
    {
      key: "subject",
      title: "Subject",
      // handleOpenFilter: handleOpenFilter,
      displayKey: "subject",
    },
    {
      key: "action",
      title: "",
      render: (item) => (
        <Box>
          <IconButton
            aria-label="more actions"
            onClick={(e) => handlePopoverOpen(e, item)}
            sx={{
              color:
                anchorEl && selectedTemplate?._id === item._id
                  ? "primary.main"
                  : "inherit",
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            anchorReference="anchorPosition"
            anchorPosition={
              anchorPositionFilter
                ? {
                    top: anchorPositionFilter.top,
                    left: anchorPositionFilter.left,
                  }
                : undefined
            }
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                boxShadow: "none",
                borderRadius: "4px",
              },
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              padding="8px"
              minWidth="150px"
            >
              <Button
                style={{
                  color: "var(--text-primary)",
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                aria-label="view"
                onClick={() => {
                  handleViewClick(selectedTemplate);
                  handlePopoverClose();
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                  gap={1}
                >
                  <ViewIcon />
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={"var(--text-primary)"}
                    fontWeight={500}
                  >
                    View
                  </Typography>
                </Box>
              </Button>

              <Button
                style={{
                  color: "var(--text-primary)",
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                aria-label="edit"
                onClick={() => {
                  onEditData && onEditData(selectedTemplate);
                  handlePopoverClose();
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                  gap={1}
                >
                  <EditIcon />
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    color={"var(--text-primary)"}
                    fontWeight={500}
                  >
                    Edit
                  </Typography>
                </Box>
              </Button>
              <Button
                style={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                aria-label="delete"
                onClick={() => {
                  handleDeleteClick(selectedTemplate);
                  handlePopoverClose();
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width=" 100%"
                  gap={1}
                >
                  <DeleteIcon />
                  <Typography
                    fontFamily={"poppins"}
                    fontSize={"16px"}
                    fontWeight={500}
                    color={"var(--text-primary)"}
                  >
                    Delete
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Popover>
        </Box>
      ),
    },
  ];

  const renderListView = () => (
    <div
      style={{
        width: "100%",
        overflow: isMobile() ? "auto" : "hidden",
        paddingBottom: 20,
        marginTop: 5,
      }}
    >
      {isMobile() ? (
        renderGridView()
      ) : (
        <TableContainer
          sx={{
            overflowY: "auto",
            paddingInline: 0.4,
            maxHeight: "100%",
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            sx={{
              border: "1px solid var(--border-color)",
            }}
          >
            <TableHead>
              <StyledTableRow>
                {TableSchema.map((col) => (
                  <StyledTableCell
                    fontSize="14px"
                    color="var(--text-primary)"
                    fontWeight={600}
                    fontFamily={"poppins"}
                    key={col.key}
                  >
                    {col.title}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {templateData.length === 0 ? (
                <StyledTableRow>
                  <TableCell colSpan={8} align="center">
                    <NoDataImage />
                  </TableCell>
                </StyledTableRow>
              ) : (
                templateData.map((template, index) => (
                  <StyledTableRow key={index}>
                    {TableSchema.map((col) => (
                      <StyledTableCell key={col.key}>
                        {col.render
                          ? col.render(template, startIndex + index)
                          : template[col.displayKey || col.key]}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );

  return (
    <div sx={{ minWidth: "100%" }}>
      <div
        style={{
          overflowY: "scroll",
          display: "flex",
          maxHeight: isMobile() ? "60vh" : `calc(100vh - 210px)`,
          width: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::WebkitScrollbar": { width: 0 },
        }}
      >
        {viewMode === "grid" ? renderGridView() : renderListView()}
      </div>
      <Pagination
        dataCount={dataCount}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        disableNext={dataCount <= rowsPerPage * (page + 1)}
      />
      <DeleteConfirmationModal
        DeleteConfirmationModal
        title={`Are you sure you want to delete ${selectedTemplate?.name} ?`}
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={() => {
          deleteData();
        }}
      />
      <EmailTemplateDetailsModal
        template={selectedTemplate}
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
      />
    </div>
  );
};

export default EmailTemplateList;
